import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AdminProvider } from "./contexts/AdminContext";
import { AdvisorSessionProvider } from "./contexts/AdvisorSessionContext";
import { NwcProvider } from "./contexts/NwcContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AuthSecurityProvider } from "./hooks/useAuthSecurity";
import Login from "./pages/Login";

// Lazy-loaded route components for code splitting
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Home = lazy(() => import("./pages/Home"));
const HomeHub = lazy(() => import("./pages/HomeHub"));
const MyTanks = lazy(() => import("./pages/MyTanks"));
const TankControlCenter = lazy(() => import("./pages/TankControlCenter"));
const Tools = lazy(() => import("./pages/Tools"));
const Trends = lazy(() => import("./pages/Trends"));
const StabilityPlan = lazy(() => import("./pages/StabilityPlan"));
const AiAdvisor = lazy(() => import("./pages/AiAdvisor"));
const FixAdvisor = lazy(() => import("./pages/advisor/StabilizationAdvisor"));
const ImproveAdvisor = lazy(() => import("./pages/advisor/OptimizationAdvisor"));
const AskAdvisor = lazy(() => import("./pages/advisor/GeneralAdvisor"));
const AdvisorResults = lazy(() => import("./pages/advisor/AdvisorResults"));
const AiAnalysisRun = lazy(() => import("./pages/advisor/AiAnalysisRun"));
const Emergency = lazy(() => import("./pages/Emergency"));
const EmergencyTriage = lazy(() => import("./pages/EmergencyTriage"));
const Library = lazy(() => import("./pages/Library"));
const Products = lazy(() => import("./pages/Products"));
const Academy = lazy(() => import("./pages/Academy"));
const AquariumStabilizationCourse = lazy(() => import("./pages/AquariumStabilizationCourse"));
const DiagnosticPredictiveMasteryCourse = lazy(() => import("./pages/DiagnosticPredictiveMasteryCourse"));
const Learn = lazy(() => import("./pages/Learn"));
const StartHere = lazy(() => import("./pages/StartHere"));
const Consult = lazy(() => import("./pages/Consult"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Account = lazy(() => import("./pages/Account"));
const BillingSuccess = lazy(() => import("./pages/BillingSuccess"));
const BillingCancel = lazy(() => import("./pages/BillingCancel"));
const PdfDelivery = lazy(() => import("./pages/PdfDelivery"));
const TankTwin = lazy(() => import("./pages/TankTwin"));
const RescueSuccess = lazy(() => import("./pages/RescueSuccess"));
const RescueNewIncident = lazy(() => import("./pages/RescueNewIncident"));
const RescueSession = lazy(() => import("./pages/RescueSession"));
const RescueSessionResolver = lazy(() => import("./pages/RescueSessionResolver"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Analytics = lazy(() => import("./pages/admin/Analytics"));
const FishSelector = lazy(() => import("./pages/tools/FishSelector"));
const CoralPlantSelector = lazy(() => import("./pages/tools/CoralPlantSelector"));
const FishCompatibility = lazy(() => import("./pages/tools/FishCompatibility"));
const TankVolume = lazy(() => import("./pages/tools/TankVolume"));
const FishStocking = lazy(() => import("./pages/tools/FishStocking"));
const WaterParamsGuide = lazy(() => import("./pages/tools/WaterParamsGuide"));
const SaltwaterMixing = lazy(() => import("./pages/tools/SaltwaterMixing"));
const WaterChangeGuide = lazy(() => import("./pages/tools/WaterChangeGuide"));
const DriftChecker = lazy(() => import("./pages/tools/DriftChecker"));
const TankTwinTool = lazy(() => import("./pages/tools/TankTwinTool"));
const NwcControlPanel = lazy(() => import("./pages/NwcControlPanel"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Security = lazy(() => import("./pages/Security"));
const Vision = lazy(() => import("./pages/Vision"));
const VisionResult = lazy(() => import("./pages/VisionResult"));
const DigitalToolbox = lazy(() => import("./pages/DigitalToolbox"));
const Terms = lazy(() => import("./pages/legal/Terms"));
const Privacy = lazy(() => import("./pages/legal/Privacy"));
const Refund = lazy(() => import("./pages/legal/Refund"));
const CookiesPage = lazy(() => import("./pages/legal/Cookies"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthSecurityProvider>
        <AdminProvider>
        <AdvisorSessionProvider>
        <NwcProvider>
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Login />} />
              <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
              <Route path="/dashboard" element={<ProtectedRoute><Home /></ProtectedRoute>} />
              <Route path="/home" element={<ProtectedRoute onboardingHandledByPage><HomeHub /></ProtectedRoute>} />
              <Route path="/start" element={<ProtectedRoute><StartHere /></ProtectedRoute>} />
              <Route path="/my-tanks" element={<ProtectedRoute><MyTanks /></ProtectedRoute>} />
              <Route path="/tank/:id" element={<ProtectedRoute><TankControlCenter /></ProtectedRoute>} />
              <Route path="/tank/:id/twin" element={<ProtectedRoute><TankTwin /></ProtectedRoute>} />
              <Route path="/tools" element={<ProtectedRoute><Tools /></ProtectedRoute>} />
              <Route path="/tools/fish-selector" element={<ProtectedRoute><FishSelector /></ProtectedRoute>} />
              <Route path="/tools/coral-plant-selector" element={<ProtectedRoute><CoralPlantSelector /></ProtectedRoute>} />
              <Route path="/tools/fish-compatibility" element={<ProtectedRoute><FishCompatibility /></ProtectedRoute>} />
              <Route path="/tools/tank-volume" element={<ProtectedRoute><TankVolume /></ProtectedRoute>} />
              <Route path="/tools/fish-stocking" element={<ProtectedRoute><FishStocking /></ProtectedRoute>} />
              <Route path="/tools/water-params-guide" element={<ProtectedRoute><WaterParamsGuide /></ProtectedRoute>} />
              <Route path="/tools/saltwater-mixing" element={<ProtectedRoute><SaltwaterMixing /></ProtectedRoute>} />
              <Route path="/tools/water-change-guide" element={<ProtectedRoute><WaterChangeGuide /></ProtectedRoute>} />
              <Route path="/tools/drift-checker" element={<ProtectedRoute><DriftChecker /></ProtectedRoute>} />
              <Route path="/tools/tank-twin" element={<ProtectedRoute><TankTwinTool /></ProtectedRoute>} />
              <Route path="/nwc" element={<ProtectedRoute><NwcControlPanel /></ProtectedRoute>} />
              <Route path="/trends" element={<ProtectedRoute><Trends /></ProtectedRoute>} />
              <Route path="/stability-plan" element={<ProtectedRoute><StabilityPlan /></ProtectedRoute>} />
              <Route path="/ai-advisor" element={<ProtectedRoute><AiAdvisor /></ProtectedRoute>} />
              <Route path="/ai-advisor/fix" element={<ProtectedRoute><FixAdvisor /></ProtectedRoute>} />
              <Route path="/ai-advisor/improve" element={<ProtectedRoute><ImproveAdvisor /></ProtectedRoute>} />
              <Route path="/ai-advisor/ask" element={<ProtectedRoute><AskAdvisor /></ProtectedRoute>} />
              <Route path="/ai-advisor/results/:sessionId" element={<ProtectedRoute><AdvisorResults /></ProtectedRoute>} />
              <Route path="/ai-advisor/run" element={<ProtectedRoute><AiAnalysisRun /></ProtectedRoute>} />
              {/* Legacy routes redirect */}
              <Route path="/ai-advisor/stabilization" element={<ProtectedRoute><FixAdvisor /></ProtectedRoute>} />
              <Route path="/ai-advisor/optimization" element={<ProtectedRoute><ImproveAdvisor /></ProtectedRoute>} />
              <Route path="/ai-advisor/general" element={<ProtectedRoute><AskAdvisor /></ProtectedRoute>} />
              <Route path="/ai-advisor/selection" element={<ProtectedRoute><ImproveAdvisor /></ProtectedRoute>} />
              {/* Anonymous-allowed: emergency landing + triage are public.
                  Auth is required only at the rescue/treatment checkout click
                  (handled inline in EmergencyTriage). Rescue session creation
                  still requires auth and happens after login via return_to. */}
              <Route path="/emergency" element={<Emergency />} />
              <Route path="/emergency/triage" element={<EmergencyTriage />} />
              <Route path="/emergency/rescue/success" element={<ProtectedRoute><RescueSuccess /></ProtectedRoute>} />
              <Route path="/emergency/rescue/new" element={<ProtectedRoute><RescueNewIncident /></ProtectedRoute>} />
              <Route path="/emergency/rescue/session" element={<ProtectedRoute><RescueSessionResolver /></ProtectedRoute>} />
              <Route path="/emergency/rescue/session/:sessionId" element={<ProtectedRoute><RescueSession /></ProtectedRoute>} />
              <Route path="/library" element={<ProtectedRoute><Library /></ProtectedRoute>} />
              <Route path="/products" element={<ProtectedRoute><Products /></ProtectedRoute>} />
              <Route path="/academy" element={<ProtectedRoute><Academy /></ProtectedRoute>} />
              <Route path="/academy/aquarium-stabilization-system" element={<ProtectedRoute><AquariumStabilizationCourse /></ProtectedRoute>} />
              <Route path="/academy/diagnostic-and-predictive-mastery" element={<ProtectedRoute><DiagnosticPredictiveMasteryCourse /></ProtectedRoute>} />
              <Route path="/learn" element={<ProtectedRoute><Learn /></ProtectedRoute>} />
              <Route path="/consult" element={<ProtectedRoute><Consult /></ProtectedRoute>} />
              <Route path="/pricing" element={<ProtectedRoute><Pricing /></ProtectedRoute>} />
              <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
              <Route path="/security" element={<ProtectedRoute><Security /></ProtectedRoute>} />
              <Route path="/vision" element={<ProtectedRoute><Vision /></ProtectedRoute>} />
              <Route path="/vision/result/:scanId" element={<ProtectedRoute><VisionResult /></ProtectedRoute>} />
              <Route path="/toolbox" element={<ProtectedRoute><DigitalToolbox /></ProtectedRoute>} />
              <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />
              <Route path="/billing/cancel" element={<ProtectedRoute><BillingCancel /></ProtectedRoute>} />
              <Route path="/pdf-delivery" element={<ProtectedRoute><PdfDelivery /></ProtectedRoute>} />
              <Route path="/admin/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/refund-policy" element={<Refund />} />
              <Route path="/cookies" element={<CookiesPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </NwcProvider>
        </AdvisorSessionProvider>
        </AdminProvider>
        </AuthSecurityProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
